import axios from "axios";
import { redirectToLogin } from "@/services/authRedirect";
import { sessionManager } from "@/services/sessionManager";
export default class ApiService {
    instance;
    constructor(config){
        this.instance = axios.create({
            ...config,
            withCredentials: true
        });
        // Add request interceptor for session management
        // NOTE: Authentication is handled via HTTP-only cookies with withCredentials: true
        // No manual token injection needed - cookies are automatically sent
        this.instance.interceptors.request.use((config)=>{
            // Check if we should reset session timers on this request
            // Exclude auth endpoints to avoid unnecessary session checks
            const isAuthEndpoint = config.url?.includes("/auth/");
            const isExtendEndpoint = config.url?.includes("/extend-session");
            const isSessionEndpoint = config.url?.includes("/session");
            // Only reset inactivity timers on non-auth API requests
            // Do NOT mark session as extended - that should only happen when we actually call /extend-session
            if (!isAuthEndpoint && !isExtendEndpoint && !isSessionEndpoint) {
                // Just reset the inactivity timers without marking session as extended
                sessionManager.resetTimers();
            }
            return config;
        }, (error)=>{
            return Promise.reject(error);
        });
        // Add response interceptor to handle auth errors globally
        this.instance.interceptors.response.use((response)=>response, (error)=>{
            // Handle rate limiting (429) or WAF block (403)
            if (error.response?.status === 429) {
                const rateLimitError = new Error("Too many requests. Please try again later.");
                return Promise.reject(rateLimitError);
            }
            // Handle WAF block that might come as 403
            if (error.response?.status === 403) {
                const message = error.response?.data?.message;
                if (message?.toLowerCase().includes("rate") || message?.toLowerCase().includes("too many")) {
                    const rateLimitError = new Error("Too many requests. Please try again later.");
                    return Promise.reject(rateLimitError);
                }
            }
            // Handle network errors (often caused by CORS issues from WAF blocks)
            if (!error.response && error.message === "Network Error") {
                const networkError = new Error("Unable to connect. You may be temporarily blocked due to too many requests. Please try again later.");
                return Promise.reject(networkError);
            }
            // Handle 401 errors - could trigger logout or redirect
            if (error.response?.status === 401) {
                const requestUrl = `${error.config?.baseURL || ""}${error.config?.url || ""}`;
                const isInitialOrInteractiveAuthRequest = requestUrl.includes("/auth/session") || requestUrl.includes("/auth/login") || requestUrl.includes("/auth/change-password-challenge") || requestUrl.includes("/auth/mfa/challenge") || requestUrl.includes("/auth/mfa/setup-challenge") || requestUrl.includes("/auth/mfa/verify-challenge");
                if (!isInitialOrInteractiveAuthRequest && typeof window !== "undefined") {
                    redirectToLogin({
                        message: "Your session has expired. Please log in again."
                    });
                }
            }
            return Promise.reject(error);
        });
    }
    get({ url, config }) {
        return this.instance.get(url, config);
    }
    post({ url, data, config }) {
        return this.instance.post(url, data, config);
    }
    patch({ url, data, config }) {
        return this.instance.patch(url, data, config);
    }
    put({ url, data, config }) {
        return this.instance.put(url, data, config);
    }
    delete({ url, config }) {
        return this.instance.delete(url, config);
    }
}
